import React, { useMemo } from 'react'
import { Emoji } from '../../../services/api'

interface EmojiTextProps {
  /** The text that may contain :emoji-name: patterns. */
  text: string
  /** Map of emoji name → Emoji object. */
  emojis: Map<string, Emoji>
  /** Optional extra className for the wrapping span. */
  className?: string
}

// Matches :emoji-name: where the name contains non-whitespace characters
const EMOJI_REGEX = /:([^\s:]+):/g

/**
 * Renders plain text with custom emoji images inline.
 * Replaces every `:name:` occurrence with the matching emoji image.
 */
export const EmojiText: React.FC<EmojiTextProps> = ({ text, emojis, className }) => {
  const parts = useMemo(() => {
    if (emojis.size === 0) return [text]

    const result: (string | React.ReactElement)[] = []
    let lastIndex = 0
    let match: RegExpExecArray | null

    // Reset regex state
    EMOJI_REGEX.lastIndex = 0

    while ((match = EMOJI_REGEX.exec(text)) !== null) {
      const emojiName = match[1]
      const emoji = emojis.get(emojiName)

      if (emoji) {
        // Push preceding text
        if (match.index > lastIndex) {
          result.push(text.slice(lastIndex, match.index))
        }
        result.push(
          <img
            key={`${emoji.id}-${match.index}`}
            src={emoji.image_url}
            alt={`:${emojiName}:`}
            title={`:${emojiName}:`}
            className="!inline !h-[1.2em] !w-[1.2em] !m-0 !p-0 align-[-0.2em] object-contain"
          />
        )
        lastIndex = match.index + match[0].length
      }
    }

    // Push remaining text
    if (lastIndex < text.length) {
      result.push(text.slice(lastIndex))
    }

    return result
  }, [text, emojis])

  if (parts.length === 1 && typeof parts[0] === 'string') {
    return <>{text}</>
  }

  return <span className={className}>{parts}</span>
}
